import classNames from "../../utils/classNames";

// Shared "hero" stat tile used at the top of dashboard pages. Mirrors the
// pattern first introduced on the Awaiting Inventory page so any new page
// can adopt the same visual grammar without copy/pasting the tone map.
//
// Heroicons are forward-ref SVG components whose `aria-hidden` is typed as
// React's `Booleanish` (`boolean | "true" | "false"`), so we fall back to
// the built-in SVG prop type rather than narrowing it manually.
export type HeadlineStatTone =
  | "amber"
  | "emerald"
  | "cyan"
  | "rose"
  | "violet"
  | "blue"
  | "slate"
  | "yellow";

const toneClasses: Record<
  HeadlineStatTone,
  { card: string; icon: string; value: string; label: string }
> = {
  amber: {
    card: "bg-amber-50 ring-amber-200",
    icon: "bg-amber-100 text-amber-600",
    value: "text-amber-900",
    label: "text-amber-700",
  },
  emerald: {
    card: "bg-emerald-50 ring-emerald-200",
    icon: "bg-emerald-100 text-emerald-600",
    value: "text-emerald-900",
    label: "text-emerald-700",
  },
  cyan: {
    card: "bg-cyan-50 ring-cyan-200",
    icon: "bg-cyan-100 text-cyan-600",
    value: "text-cyan-900",
    label: "text-cyan-700",
  },
  rose: {
    card: "bg-rose-50 ring-rose-200",
    icon: "bg-rose-100 text-rose-600",
    value: "text-rose-900",
    label: "text-rose-700",
  },
  violet: {
    card: "bg-violet-50 ring-violet-200",
    icon: "bg-violet-100 text-violet-600",
    value: "text-violet-900",
    label: "text-violet-700",
  },
  blue: {
    card: "bg-blue-50 ring-blue-200",
    icon: "bg-blue-100 text-blue-600",
    value: "text-blue-900",
    label: "text-blue-700",
  },
  slate: {
    card: "bg-slate-50 ring-slate-200",
    icon: "bg-slate-100 text-slate-600",
    value: "text-slate-900",
    label: "text-slate-700",
  },
  yellow: {
    card: "bg-yellow-50 ring-yellow-200",
    icon: "bg-yellow-100 text-yellow-600",
    value: "text-yellow-900",
    label: "text-yellow-700",
  },
};

const toneRingActive: Record<HeadlineStatTone, string> = {
  amber: "ring-2 ring-amber-500",
  emerald: "ring-2 ring-emerald-500",
  cyan: "ring-2 ring-cyan-500",
  rose: "ring-2 ring-rose-500",
  violet: "ring-2 ring-violet-500",
  blue: "ring-2 ring-blue-500",
  slate: "ring-2 ring-slate-500",
  yellow: "ring-2 ring-yellow-500",
};

interface HeadlineStatProps {
  value: string;
  label: string;
  tone: HeadlineStatTone;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  sublabel?: string;
  /** When provided, the tile becomes a clickable filter toggle. */
  onClick?: () => void;
  /** Highlights the tile with a stronger ring when the filter is active. */
  active?: boolean;
  title?: string;
  /** Tight layout for dense grids — smaller icon/value/label/padding. */
  compact?: boolean;
}

export default function HeadlineStat({
  value,
  label,
  tone,
  icon: Icon,
  sublabel,
  onClick,
  active,
  title,
  compact,
}: HeadlineStatProps) {
  const theme = toneClasses[tone];
  const clickable = typeof onClick === "function";

  const body = (
    <>
      <span
        className={classNames(
          "flex flex-none items-center justify-center rounded-lg",
          compact ? "h-8 w-8" : "h-10 w-10",
          theme.icon
        )}
      >
        <Icon
          className={compact ? "h-4 w-4" : "h-5 w-5"}
          aria-hidden={true}
        />
      </span>
      <div className="min-w-0 text-left">
        <p
          className={classNames(
            "font-bold leading-none",
            compact ? "text-lg" : "text-2xl",
            theme.value
          )}
        >
          {value}
        </p>
        <p
          className={classNames(
            "font-semibold uppercase tracking-wide",
            compact ? "mt-0.5 text-[10px]" : "mt-1 text-xs",
            theme.label
          )}
        >
          {label}
        </p>
        {sublabel && (
          <p
            className={classNames(
              compact ? "mt-0.5 text-[10px]" : "mt-0.5 text-[11px]",
              theme.label
            )}
          >
            {sublabel}
          </p>
        )}
      </div>
    </>
  );

  const baseClasses = classNames(
    "flex items-center rounded-xl shadow-sm ring-1 ring-inset",
    compact ? "gap-2 px-2.5 py-2" : "gap-3 px-4 py-3",
    theme.card,
    active ? toneRingActive[tone] : ""
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        aria-pressed={active}
        className={classNames(
          baseClasses,
          "cursor-pointer transition-transform hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500/40"
        )}
      >
        {body}
      </button>
    );
  }

  return (
    <div className={baseClasses} title={title}>
      {body}
    </div>
  );
}
